package co.maytes.standupbot.dashboard.features.analytics

import androidx.compose.runtime.Composable
import co.maytes.standupbot.dashboard.components.feedback.EmptyState
import co.maytes.standupbot.dashboard.components.widgets.Card
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideBarChart
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.lineHeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.px

@Composable
fun ChartShell(
    title: String,
    subtitle: String,
    eyebrow: String,
    isEmpty: Boolean,
    legend: List<LegendItem> = emptyList(),
    emptyTitle: String = "Not enough data yet",
    emptyMessage: String = "Your charts fill in as standups post — check back once a few runs have landed.",
    emptyIcon: (@Composable () -> Unit)? = null,
    content: @Composable () -> Unit,
) {
    Card(modifier = Modifier.fillMaxWidth()) {
        Column(modifier = Modifier.fillMaxWidth().gap(Spacing.md)) {
            Row(
                verticalAlignment = Alignment.Top,
                modifier = Modifier.fillMaxWidth().styleModifier {
                    property("justify-content", "space-between")
                    property("flex-wrap", "wrap")
                    property("gap", "12px")
                },
            ) {
                Column(modifier = Modifier.gap(Spacing.xxs)) {
                    SpanText(
                        text = eyebrow,
                        modifier = Modifier
                            .fontSize(Typography.caption.size)
                            .fontWeight(600)
                            .color(Palette.textTertiary)
                            .styleModifier {
                                property("text-transform", "none")
                                property("letter-spacing", "0.07em")
                            },
                    )
                    SpanText(
                        text = title,
                        modifier = Modifier
                            .margin(top = 2.px)
                            .fontSize(Typography.h3.size)
                            .fontWeight(Typography.h3.weight)
                            .lineHeight(Typography.h3.lineHeight)
                            .color(Palette.textPrimary)
                            .styleModifier { property("letter-spacing", "-0.01em") },
                    )
                    SpanText(
                        text = subtitle,
                        modifier = Modifier
                            .fontSize(Typography.bodySmall.size)
                            .lineHeight(Typography.bodySmall.lineHeight)
                            .color(Palette.textSecondary)
                            .styleModifier { property("max-width", "46ch") },
                    )
                }
                if (legend.isNotEmpty() && !isEmpty) {
                    Box(modifier = Modifier.margin(top = 2.px)) { ChartLegend(legend) }
                }
            }

            if (isEmpty) {
                ChartEmptyState(emptyTitle, emptyMessage, emptyIcon)
            } else {
                Box(modifier = Modifier.fillMaxWidth().margin(top = Spacing.xs)) { content() }
            }
        }
    }
}

@Composable
fun ChartEmptyState(
    title: String = "Not enough data yet",
    message: String = "Your charts fill in as standups post — check back once a few runs have landed.",
    icon: (@Composable () -> Unit)? = null,
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .borderRadius(Radius.lg)
            .padding(topBottom = Spacing.sm)
            .styleModifier {
                property("background", Palette.surfaceSubtle.cssRgb())
                property("border", "1px solid ${Palette.borderSubtle.cssRgb()}")
                property("background-image",
                    "radial-gradient(${Palette.border.cssRgb()} 0.8px, transparent 0.8px)")
                property("background-size", "16px 16px")
                property("background-position", "center")
            },
        contentAlignment = Alignment.Center,
    ) {
        EmptyState(
            title = title,
            subtitle = message,
            icon = icon ?: { LucideBarChart(size = 26) },
        )
    }
}
